<template>
  <div :style="headerWrapperStyle" class="dashboard-header-wrapper">
    <div ref="header" class="dashboard-header">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h5 class="mb-0">Performance Dashboard</h5>
        </div>
        <div class="d-flex align-items-center">
          <div class="me-2">
            <b-dropdown
                block
                class="zone-dropdown"
                menu-class="w-100"
            >
              <template #button-content>
                {{ zoneDropdownText }}
              </template>
              <b-dropdown-form>
                <b-form-checkbox v-model="selectAll" @change="toggleZoneAll">
                  {{ $t('All {something}', {something: $t('zones.singular')}) }}
                </b-form-checkbox>

                <b-form-checkbox-group
                    v-model="selectedZoneId"
                    :options="zones"
                    class="zone-dropdown"
                    stacked
                ></b-form-checkbox-group>

                <div v-if="!isValid" class="text-danger mt-2">
                  <b-icon icon="exclamation-circle-fill"></b-icon>
                  Minimal memilih 1 region
                </div>
              </b-dropdown-form>
            </b-dropdown>
          </div>
          <div class="d-flex align-items-stretch">
            <date-range-picker
                ref="picker"
                v-model="selectedDate"
                :auto-apply="false"
                :linkedCalendars="linkedCalendars"
                :locale-data="localeData"
                :max-span="maxSpan"
                :maxDate="maxDate"
                :minDate="minDate"
                :ranges="dateRangePickerShortcuts"
                :showDropdowns="true"
                opens="left"
                @select="onDateRangeSelect"
                @update="validateDateRange"
            >
              <template v-slot:input="picker" style="min-width: 350px;">
                <i class="fa fa-calendar me-2"></i>
                {{ $moment(picker.startDate).format('DD MMM YYYY') }} -
                {{ $moment(picker.endDate).format('DD MMM YYYY') }}
              </template>

              <template v-slot:footer>
                <div class="daterange-limit-info">
                  <div class="range-progress-container">
                    <div
                        :class="{ 'warning': isNearLimit, 'danger': isOverOneYear }"
                        :style="{ width: `${getProgressPercentage(currentDateRange)}%` }"
                        class="range-progress-bar"
                    ></div>
                  </div>
                  <div
                      :class="{ 'warning-text': isNearLimit, 'danger-text': isOverOneYear }"
                      class="range-limit-text"
                  >
                    <span v-if="isOverOneYear">
                      <b-icon-exclamation-circle/> Maksimal rentang pemilihan adalah 1 tahun
                    </span>
                    <span v-else-if="isNearLimit">
                      Mendekati batas 1 tahun ({{ getRemainingDays(currentDateRange) }} hari tersisa)
                    </span>
                    <span v-else>
                      {{ getRemainingDays(currentDateRange) }} hari tersisa dari batas 1 tahun
                    </span>
                  </div>
                </div>
                <div class="daterange-footer">
                  <div class="selected-range me-2">
                    {{ $moment(currentDateRange.startDate).format('DD MMM YYYY') }} -
                    {{ $moment(currentDateRange.endDate).format('DD MMM YYYY') }}
                  </div>
                  <div class="action-buttons">
                    <b-button class="font-size-16 btn-reset" variant="light" @click="resetDateRange">Reset</b-button>
                    <b-button class="font-size-16 btn-cancel" variant="outline-dark" @click="cancelDateRange">Batal
                    </b-button>
                    <b-button
                        :disabled="isOverOneYear"
                        class="font-size-16 btn-apply"
                        variant="primary"
                        @click="applyDateRange"
                    >{{ $t('Apply') }}
                    </b-button>
                  </div>
                </div>
              </template>
            </date-range-picker>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import {dateRangePickerConfig} from "@/router/views/dashboards/components/dateRangePickerConfig";

export default {
  name: 'DashboardHeader',
  components: {
    DateRangePicker,
  },
  props: {
    zoneId: {
      type: Array,
      default() {
        return [1, 2, 3]
      }
    },
    dateRange: {
      type: Array,
      default() {
        return [this.$moment().startOf('years').toDate(), this.$moment().toDate()]
      }
    }
  },
  data() {
    return {
      ...dateRangePickerConfig,
      isValid: true,
      selectAll: this.lodash.isEqual(this.zoneId, [1, 2, 3]),
      selectedZoneId: this.zoneId,
      selectedDate: {
        startDate: this.dateRange[0],
        endDate: this.dateRange[1],
      },
      currentDateRange: {
        startDate: this.dateRange[0],
        endDate: this.dateRange[1],
      },
      originalDate: {
        startDate: null,
        endDate: null
      },
      headerHeight: 0,
      sidenavWidth: 250,
      resizeObserver: null,
      zones: [
        {text: `${this.lodash.upperFirst(this.$t('zones.singular'))} 1`, value: 1},
        {text: `${this.lodash.upperFirst(this.$t('zones.singular'))} 2`, value: 2},
        {text: `${this.lodash.upperFirst(this.$t('zones.singular'))} 3`, value: 3},
      ],
      showRegionError: false,
      isOverOneYear: false,
      isNearLimit: false
    };
  },
  watch: {
    selectedZoneId(newVal, oldVal) {
      if (this.lodash.isEqual(newVal, oldVal)) {
        return
      }
      this.validateSelection();
      if (!this.isValid) {
        return
      }
      this.selectAll = [1, 2 , 3].every(val => newVal.includes(val));

      this.$emit('zone-id-changed', this.lodash.cloneDeep(newVal.sort()));
    },
    selectedDate: {
      handler(newVal) {
        this.$emit('date-range-changed', [newVal.startDate, newVal.endDate]);
      },
      deep: true,
    }
  },
  computed: {
    zoneDropdownText() {
      if (this.selectedZoneId.length === 0) {
        return this.lodash.startCase(this.$t('Select {something}', {something: this.$t('zones.plural')}));
      }
      if (this.selectAll) {
        return this.lodash.startCase(this.$t('All {something}', {something: this.$t('zones.plural')}));
      }
      let joinedZone = [...this.selectedZoneId].sort().join(', ')
      let lastIndex = joinedZone.lastIndexOf(', ');
      if (lastIndex === -1) {
        return `${this.lodash.upperFirst(this.$t('zones.singular'))} ${joinedZone}`
      }
      joinedZone = joinedZone.slice(0, lastIndex) + ' & ' + joinedZone.slice(lastIndex + 1)
      return `${this.lodash.upperFirst(this.$t('zones.singular'))} ${joinedZone}`
    },
    dateRangePickerShortcuts() {
      const today = this.$moment().startOf('day').toDate();
      const lastWeek = this.$moment().startOf('day').subtract(1, 'weeks').toDate();
      const threeMonthsAgo = this.$moment().subtract(3, 'months').toDate();
      const oneYearAgo = this.$moment().subtract(1, 'year').toDate();
      const thisMonthStart = this.$moment().startOf('month').toDate();
      const thisMonthEnd = this.$moment().endOf('month').toDate();
      const thisYearEnd = this.$moment().endOf('year').toDate();


      return {
        [this.lodash.upperFirst(this.$t('dashboards.date_shortcuts.today'))]: [today, today],
        [this.lodash.upperFirst(this.$t('dashboards.date_shortcuts.this_week'))]: [lastWeek, today],
        [this.lodash.upperFirst(this.$t('dashboards.date_shortcuts.this_month'))]: [thisMonthStart, thisMonthEnd],
        [this.lodash.upperFirst(this.$t('dashboards.date_shortcuts.3_months_ago'))]: [threeMonthsAgo, today],
        [this.lodash.upperFirst(this.$t('dashboards.date_shortcuts.1_year_ago'))]: [oneYearAgo, thisYearEnd],
      }
    },
    headerWrapperStyle() {
      return {
        width: `calc(100% - ${this.sidenavWidth}px)`,
        marginLeft: `-24px`,
        marginTop: `-24px`,
        border: '1px solid #EAECF0',
        borderLeft: '0',
        borderRight: '0',
      };
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.detectSidenavWidth();
      this.setupResizeObserver();
      window.addEventListener('resize', this.detectSidenavWidth);

      this.originalDate = {...this.selectedDate};
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.detectSidenavWidth);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },
  methods: {
    toggleZoneAll(checked) {
      if (checked) {
        this.selectedZoneId = this.zones.map(({value}) => value);
      } else {
        this.selectedZoneId = [];
      }
      this.validateSelection();
    },
    validateSelection() {
      this.isValid = this.selectedZoneId.length > 0;
    },
    onDateRangeSelect(val) {
      this.currentDateRange = val;
      this.validateDateRange(this.currentDateRange);
    },
    validateDateRange(newRange) {
      if (newRange.startDate && newRange.endDate) {
        let diffDays = Math.abs(this.$moment(newRange.startDate).diff(this.$moment(newRange.endDate), 'days'));

        this.isOverOneYear = diffDays > 365;
        this.isNearLimit = !this.isOverOneYear && diffDays >= 292;
      } else {
        this.isOverOneYear = false;
        this.isNearLimit = false;
      }
    },

    getProgressPercentage(dateRange) {
      if (!dateRange || !dateRange.startDate || !dateRange.endDate) return 0;

      const start = new Date(dateRange.startDate);
      const end = new Date(dateRange.endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      return Math.min(100, (diffDays / 365) * 100);
    },

    getRemainingDays(dateRange) {
      if (!dateRange || !dateRange.startDate || !dateRange.endDate) return 365;

      const start = new Date(dateRange.startDate);
      const end = new Date(dateRange.endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      return Math.max(0, 365 - diffDays);
    },
    resetDateRange() {
      this.selectedDate = {
        startDate: this.dateRange[0],
        endDate: this.dateRange[1],
      };
      this.currentDateRange = this.selectedDate
      this.validateDateRange(this.currentDateRange)
    },

    cancelDateRange() {
      this.resetDateRange();
      this.$refs.picker.togglePicker(false);
    },

    applyDateRange() {
      if (this.isOverOneYear) {
        return
      }
      if (this.currentDateRange.startDate > this.currentDateRange.endDate) {
        this.$set(this, 'selectedDate', {
          startDate: this.currentDateRange.endDate,
          endDate: this.currentDateRange.startDate,
        })
      } else {
        this.$set(this, 'selectedDate', {
          startDate: this.currentDateRange.startDate,
          endDate: this.currentDateRange.endDate,
        })
      }
      this.$refs.picker.togglePicker(false)
    },

    detectSidenavWidth() {
      const sidenav = document.querySelector('.vertical-menu'); // sesuaikan dengan class sidenav Anda
      if (sidenav) {
        const newWidth = sidenav.offsetWidth;
        if (this.sidenavWidth !== newWidth) {
          this.sidenavWidth = newWidth;
        }
      }
    },

    setupResizeObserver() {
      // ResizeObserver API untuk mendeteksi perubahan ukuran pada vertical-menu
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(entries => {
          for (let entry of entries) {
            const newWidth = entry.contentRect.width;
            if (this.sidenavWidth !== newWidth) {
              this.sidenavWidth = newWidth;
            }
          }
        });

        const sidenav = document.querySelector('.vertical-menu');
        if (sidenav) {
          this.resizeObserver.observe(sidenav);
        } else {
          window.console.warn('Vertical menu element not found. Observer not attached.');

          // Jika elemen belum ada, coba lagi setelah beberapa waktu
          setTimeout(() => {
            const retryElement = document.querySelector('.vertical-menu');
            if (retryElement && this.resizeObserver) {
              this.resizeObserver.observe(retryElement);
            }
          }, 1000);
        }
      } else {
        window.console.warn('ResizeObserver not supported in this browser. Falling back to manual detection.');
        // Fallback untuk browser yang tidak mendukung ResizeObserver
        setInterval(this.detectSidenavWidth, 500);
      }
    }
  }
};
</script>

<style scoped>
.zone-dropdown {
  min-width: 180px !important;

  button {
    color: black !important;
    background-color: white;
    border: 1px solid #D0D5DD;
    font: 400 16px Lato, sans-serif;
  }
}

.zone-dropdown.show {
  min-width: 160px;

  button {
    color: black !important;
    background-color: white;
    border: 1px solid #D0D5DD;
    font: 400 16px Lato, sans-serif;
  }

  .b-dropdown-form {
    padding: 4px 16px;
  }

  .custom-checkbox.custom-control {
    margin: 16px 0;

    input {
      height: 16px;
      width: 16px;
    }

    label {
      font: 400 16px Lato, sans-serif;
    }
  }

  .custom-checkbox .custom-control-label::before {
    border-radius: 4px !important;
  }
}

.dashboard-header-wrapper {
  position: fixed;
  z-index: 10;
}

.dashboard-header {
  background-color: #fff;
  padding: 15px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  /* z-index: 10; */
  width: 100%;
}

.dashboard-header.fixed {
  position: fixed;
  top: 75px;
  left: 0;
  right: 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-radius: 0;
  padding: 15px 20px;
}

/* Styling untuk range limit info */
::v-deep {
  .vue-daterange-picker {
    .reportrange-text {
      border: 1px solid #D0D5DD;
      border-radius: 8px;
      color: black;
      font: 400 16px Lato, sans-serif;
      padding: 8px 16px;
    }

    .daterangepicker {
      font-size: 16px;

      .ranges {
        li {
          font: 400 16px Lato, sans-serif;
        }
      }

      .action-buttons {
        btn {
          font: 400 16px Lato, sans-serif;
        }
      }
    }
  }

  .daterange-limit-info {
    padding: 10px 12px;
    border-top: 1px solid #eee;
    margin-top: 5px;
  }

  .range-progress-container {
    height: 6px;
    background-color: #E0E0E0;
    border-radius: 3px;
    margin: 8px 0;
    width: 100%;
  }

  .range-progress-bar {
    height: 100%;
    border-radius: 3px;
    background-color: #4CAF50; /* Hijau untuk normal */
    transition: width 0.3s, background-color 0.3s;
  }

  .range-progress-bar.warning {
    background-color: #FFC107; /* Kuning untuk mendekati batas */
  }

  .range-progress-bar.danger {
    background-color: #F44336; /* Merah untuk melebihi batas */
  }

  .range-limit-text {
    color: #757575;
    margin-top: 4px;
    margin-bottom: 12px; /* Tambahkan jarak ke bawah */
  }

  .warning-text {
    color: #F57C00; /* Oranye */
  }

  .danger-text {
    color: #D32F2F; /* Merah */
    font-weight: bold;
  }

  /* Styling untuk tombol yang dinonaktifkan */

  .daterangepicker .btn-apply:disabled {
    opacity: 0.6 !important;
    cursor: not-allowed !important;
    background-color: #cccccc !important;
  }

  /* Styling untuk footer dengan tanggal yang dipilih dan tombol-tombol */

  .daterange-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 8px 12px;
    border-top: 1px solid #eee;
  }

  .selected-range {
    color: #333;
  }

  .action-buttons {
    display: flex;
    gap: 8px;
  }

  .btn-reset {
    padding: 6px 12px;
    background-color: #fff;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    color: #333;
  }

  .btn-cancel {
    padding: 6px 12px;
    background-color: #f8f9fa;
    border: 1px solid #ccc;
    border-radius: 4px;
    cursor: pointer;
    color: #333;
  }

  .btn-apply {
    padding: 6px 12px;
    background-color: #0d6efd;
    border: 1px solid #0d6efd;
    border-radius: 4px;
    cursor: pointer;
    color: white;
  }

  .btn-apply:disabled {
    background-color: #cccccc;
    border-color: #cccccc;
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-reset:hover,
  .btn-cancel:hover {
    background-color: #f0f0f0;
  }

  .btn-apply:hover:not(:disabled) {
    background-color: #0b5ed7;
  }
}


/* Styling lainnya tetap sama */
.zone-dropdown {
  min-width: 160px;
}

.zone-dropdown ::v-deep .dropdown-toggle {
  text-align: left;
  padding-right: 30px;
  position: relative;
}

.zone-dropdown ::v-deep .dropdown-toggle::after {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
}

.zone-dropdown ::v-deep .dropdown-menu {
  padding: 4px 0;
}

.zone-dropdown-content {
  position: relative;
  padding-left: 4px; /* Space for the blue line */
}

.zone-dropdown-content::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background-color: #0d6efd;
}

.region-item {
  padding: 10px 16px;
  border-bottom: 1px solid #f0f0f0;
}

.region-item:last-child {
  border-bottom: none;
}

.region-checkbox {
  font-weight: normal;
  width: 100%;
}

/* Custom styling for checkboxes */
.region-checkbox ::v-deep .custom-control-label {
  width: 100%;
  cursor: pointer;
}

.region-checkbox ::v-deep .custom-control-label::before {
  border-radius: 3px;
  background-color: #fff;
  border: 1px solid #adb5bd;
}

.region-checkbox ::v-deep .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

@media (max-width: 768px) {
  .dashboard-header {
    z-index: 1000;
  }

  .dashboard-header .me-2 {
    margin-right: 0 !important;
    margin-bottom: 10px;
  }
}
</style>